import {
  FiltrazioneDto,
  getDownloadRapportinoFiltrazioneQueryKey,
  getGetNodoPortaleQueryKey,
  getGetNodoQueryKey,
  NodoDto,
  useUpdateFiltrazioni,
} from "@/api";
import Box from "@/elements/Box";
import Typography from "@/elements/Typography";
import Table from "@/elements/Table";
import moment from "moment/moment";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import Button from "@/elements/Button";
import { Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FiltrazioneDialog from "@/components/FiltrazioneDialog";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import FileDownload from "@/components/FileField/FileDownload";
import { portaleConfig } from "@/portaliConfig";

const columnHelper = createColumnHelper<FiltrazioneDto>();

export default function FiltrazioniTableCard({
  nodo,
  filtrazioni,
  canEdit = false,
}: {
  nodo: NodoDto;
  filtrazioni: FiltrazioneDto[];
  canEdit?: boolean;
}) {
  const { t } = useTranslation();

  const [filtrazioneDialogOptions, setFiltrazioneDialogOptions] = useState<{
    open: boolean;
    filtrazione?: FiltrazioneDto;
  }>({ open: false });

  const editing = canEdit;

  const queryClient = useQueryClient();
  const { mutate } = useUpdateFiltrazioni();

  const deleteFiltrazione = useCallback(
    (filtrazione: FiltrazioneDto) => {
      const data = moment(filtrazione.data).format("L");
      const message = t("filtrazioniTable.confermaElimina", { data });
      if (!window.confirm(message)) {
        return;
      }

      mutate(
        {
          id: nodo.id,
          data: [
            {
              id: filtrazione.id,
              changeType: "deleted",
              entity: filtrazione,
            },
          ],
        },
        {
          onSuccess: () => {
            queryClient
              .invalidateQueries(getGetNodoPortaleQueryKey(portaleConfig.id, nodo.id))
              .then(() => {
                toast.success(t("filtrazioniTable.toast.eliminata"));
              });
          },
          onError: () => {
            toast.error(t("filtrazioniTable.toast.eliminazioneErrore"));
          },
        }
      );
    },
    [queryClient, nodo.id, mutate, t]
  );

  const columns = useMemo<ColumnDef<FiltrazioneDto, any>[]>(() => {
    const columns = [
      columnHelper.accessor("data", {
        header: t("filtrazioniTable.colonne.data"),
        cell: ({ row }) => moment(row.original.data).format("L"),
      }),

      columnHelper.display({
        header: t("filtrazioniTable.colonne.rapportino"),
        cell: ({ row }) => (
          <FileDownload
            file={row.original.fileRapportino}
            downloadUrl={
              getDownloadRapportinoFiltrazioneQueryKey(
                nodo.id,
                row.original.id
              )[0]
            }
          />
        ),
      }),

      editing &&
        columnHelper.display({
          id: "buttons",
          header: "",
          size: 100,
          maxSize: 100,
          cell: ({ row }) => {
            return (
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="flex-end"
                gap={1}
              >
                <Tooltip title={t("azioni.elimina")}>
                  <IconButton
                    onClick={() => {
                      deleteFiltrazione(row.original);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>

                <Tooltip title={t("azioni.modifica")}>
                  <IconButton
                    onClick={() => {
                      setFiltrazioneDialogOptions({
                        open: true,
                        filtrazione: row.original,
                      });
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          },
        }),
    ].filter((x) => x);
    return columns as Exclude<(typeof columns)[number], boolean>[];
  }, [editing, deleteFiltrazione, t]);

  return (
    <>
      <Box flex={1}>
        <Box display="flex" flexDirection="column" height="100%" gap={1}>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            gap={1}
          >
            <Typography variant="h5" color="text" fontWeight="medium">
              {t("filtrazioniTable.titolo")}
            </Typography>

            {editing && (
              <Button
                color="primary"
                size="small"
                onClick={() => {
                  setFiltrazioneDialogOptions({ open: true });
                }}
              >
                {t("azioni.aggiungi")}
              </Button>
            )}
          </Box>

          <Table
            columns={columns}
            data={filtrazioni}
            initialSort={[{ id: "data", desc: true }]}
            noRowsText={t("filtrazioniTable.noRows")}
            mt={1}
          />
        </Box>
      </Box>

      <FiltrazioneDialog
        nodo={nodo}
        open={filtrazioneDialogOptions.open}
        filtrazione={filtrazioneDialogOptions.filtrazione}
        onClose={() => {
          setFiltrazioneDialogOptions({ open: false });
        }}
      />
    </>
  );
}
